import { useEffect, useRef, useState } from "react"
import { addMyPick, removeMyPick } from "@/services/jobs"
import { useSessionStore } from "@/store/sessionStore"
import { describeWriteError } from "@/utils/firestoreWrite"
import type { CardId } from "@/types"

/*
 * One person's hand, as they experience it: what Firestore has confirmed, with
 * the decisions they have taken since then already applied.
 *
 * The document is made safe by `addMyPick`/`removeMyPick`, which are server-side
 * array transforms — two of them in flight at once both land. That is only half
 * of it. The screen still reads the hand from the last snapshot the listener
 * delivered, and every guard the round has is a question about that array: is
 * this card already held, is the hand full. Dealing quickly asks those questions
 * faster than the echo comes back, so a run of five swipes checks the cap five
 * times against an empty hand and keeps a sixth card into a hand of five.
 *
 * `asked` is the fix: the latest thing this client requested, per card, laid
 * over whatever the listener last said. Because it is keyed by card and holds
 * only the LAST request for that card, replaying it is idempotent whatever order
 * the echoes arrive in — an entry the snapshot has caught up with changes
 * nothing, which is why it can be dropped lazily rather than raced.
 */

type Intent = "in" | "out"

export interface CardHand {
  /** Confirmed picks plus this client's outstanding decisions, in hand order. */
  hand: CardId[]
  capReached: boolean
  keep: (cardId: CardId) => void
  drop: (cardId: CardId) => void
  toggle: (cardId: CardId) => void
}

type Requested = Record<string, Intent>

const NOTHING_ASKED: Requested = {}

function applyIntent(confirmed: readonly CardId[], asked: Requested): CardId[] {
  const entries = Object.entries(asked)
  if (entries.length === 0) return [...confirmed]

  const hand = confirmed.filter((cardId) => asked[cardId] !== "out")
  /*
   * Appended in the order they were asked for, which is where `arrayUnion` puts
   * them too — a slot must not jump position when the echo replaces the guess.
   */
  for (const [cardId, want] of entries) {
    if (want === "in" && !hand.includes(cardId)) hand.push(cardId)
  }
  return hand
}

/**
 * Drops every request the server has since confirmed. Not needed for the hand to
 * be right, but it keeps a second tab on the same guest id from re-asserting a
 * card that tab has dropped, and stops the map growing across a long round.
 */
function outstanding(asked: Requested, confirmed: readonly CardId[]): Requested {
  const left: Requested = {}
  for (const [cardId, want] of Object.entries(asked)) {
    if ((want === "in") !== confirmed.includes(cardId)) left[cardId] = want
  }
  return left
}

/**
 * @param confirmed this participant's picks as the listener last delivered them
 * @param cap `config.handCap`; 0 means no cap
 */
export function useCardHand(
  sessionId: string,
  jobId: string,
  participantId: string | null,
  confirmed: readonly CardId[],
  cap: number
): CardHand {
  const setError = useSessionStore((s) => s.setError)

  /*
   * Stamped with whose hand, and which job's, it was asked about. A different
   * job or a different seat is a different hand, and anything asked for about
   * the old one is simply ignored — cheaper and safer than clearing it from an
   * effect, which would render the stale overlay once before dropping it.
   */
  const scope = `${sessionId}/${jobId}/${participantId ?? ""}`
  const [asked, setAsked] = useState<{ scope: string; cards: Requested }>({
    scope,
    cards: NOTHING_ASKED,
  })
  const mine = asked.scope === scope ? asked.cards : NOTHING_ASKED

  const hand = applyIntent(confirmed, mine)

  /*
   * The same hand, readable synchronously. `hand` above is only as current as
   * the last commit, and the deal can take two decisions between commits — the
   * cap and the already-held test have to see the first one when they judge the
   * second, or the guard they exist to be is not there.
   */
  const handRef = useRef<CardId[]>(hand)
  useEffect(() => {
    handRef.current = hand
  })

  const request = (cardId: CardId, want: Intent) => {
    if (!participantId) return

    setAsked((current) => {
      const cards = current.scope === scope ? outstanding(current.cards, confirmed) : {}
      // Deleted before it is set, so the map's key order stays the order the
      // decisions were taken in — which is the order the hand renders in.
      delete cards[cardId]
      cards[cardId] = want
      return { scope, cards }
    })
    handRef.current =
      want === "in"
        ? [...handRef.current.filter((id) => id !== cardId), cardId]
        : handRef.current.filter((id) => id !== cardId)

    const write = want === "in" ? addMyPick : removeMyPick
    void write(sessionId, jobId, participantId, cardId).catch((error: unknown) => {
      setError(describeWriteError(error))
      // The card never landed: take the promise back off the screen rather than
      // leave the hand showing something the document does not hold.
      setAsked((current) => {
        // Superseded — they have asked for something else for this card since,
        // and that request owns the slot now.
        if (current.scope !== scope || current.cards[cardId] !== want) return current
        const cards = { ...current.cards }
        delete cards[cardId]
        return { scope, cards }
      })
    })
  }

  const keep = (cardId: CardId) => {
    const held = handRef.current
    if (held.includes(cardId)) return
    if (cap > 0 && held.length >= cap) return
    request(cardId, "in")
  }

  const drop = (cardId: CardId) => {
    if (!handRef.current.includes(cardId)) return
    request(cardId, "out")
  }

  const toggle = (cardId: CardId) => {
    if (handRef.current.includes(cardId)) drop(cardId)
    else keep(cardId)
  }

  return { hand, capReached: cap > 0 && hand.length >= cap, keep, drop, toggle }
}
